import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useNavStore } from '@shared/app-shell/nav-state';
import { useThemeStore } from '@shared/theme/ThemeProvider';
import { copy } from '@shared/copy';
import { cn } from '@shared/ui/cn';
import { MenuIcon, MoonIcon, SunIcon } from '@shared/ui/icons';
import { PlusIcon } from '@shared/ui/icons/nav';
import { BrandLockup } from '@shared/ui/icons/BrandMark';

import { AccountMenu } from './AccountMenu';
import { chromeCopy } from './copy';
import { InboxHandle } from './InboxHandle';

/**
 * Five controls beside the lockup — navigation handle, theme toggle,
 * notification control, create action, account face.
 *
 * THERE IS NO SEARCH FIELD HERE. The console carried one that submitted to
 * `/content?q=…`, and it is gone by decision rather than by regression: the
 * operator surfaces that need to narrow a list carry their own filter beside
 * that list — the content list's publish-state and type facets, the people
 * list's own field, and the pickers that attach a title to a shelf's source
 * set, an editorial post or a credit. A bar-level field over one of those lists was a
 * second route into a surface that already filters itself, on every route
 * including the ones it could not search.
 *
 * The service side is UNTOUCHED: `GET /admin/contentItems?q=` still answers,
 * because the three pickers above are its real callers and removing it would
 * leave them listing twenty rows with no way to reach the twenty-first.
 *
 * The bar is sticky and never moves: past 64 px of scroll it **solidifies**, and
 * only the background and the border transition. Changing its height would make
 * the content beneath it jump on every scroll.
 *
 * Five 44 px targets and four gaps do not fit a 320 px window, so the base tier
 * takes the narrowest gap step and restores the normal one from `--bp-sm`. The
 * target size is not what gives way — that would break 2.5.8 — and no control is
 * dropped, because every one of the five is a catalog row.
 *
 * The wordmark is **clipped**, never `display: none`: the latter strips the text
 * from the accessibility tree and leaves the link relying on an attribute the
 * next edit removes.
 */
const SOLID_AFTER_PX = 64;

export function Topbar({
  navId,
  navToggleId,
  signOut,
}: {
  navId: string;
  navToggleId: string;
  signOut: () => void;
}) {
  const toggleNav = useNavStore((state) => state.toggle);
  const navOpen = useNavStore((state) => state.open);
  const theme = useThemeStore((state) => state.theme);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);

  const [solid, setSolid] = useState(false);

  useEffect(() => {
    let ticking = false;

    function apply() {
      ticking = false;
      setSolid(window.scrollY > SOLID_AFTER_PX);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      role="banner"
      aria-label={chromeCopy.topbar.label}
      data-solid={solid ? 'true' : 'false'}
      className={cn(
        'sticky z-50 flex items-center gap-1 border-b border-transparent px-gutter py-3 transition-[background-color,border-color] duration-fast ease-standard sm:gap-3',
        '[inset-block-start:0]',
        'data-[solid=true]:border-border data-[solid=true]:shadow-1 data-[solid=true]:[backdrop-filter:blur(14px)_saturate(1.4)] data-[solid=true]:[background:color-mix(in_srgb,var(--surface)_92%,transparent)]',
      )}
    >
      <button
        id={navToggleId}
        type="button"
        onClick={toggleNav}
        aria-expanded={navOpen}
        aria-controls={navId}
        aria-label={navOpen ? chromeCopy.nav.close : chromeCopy.nav.open}
        className="hit-target flex items-center justify-center rounded-sm text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text lg:hidden"
      >
        <MenuIcon />
      </button>

      <Link
        to="/"
        aria-label={chromeCopy.brandLink}
        className="flex shrink-0 items-center gap-2 text-ink no-underline"
      >
        {/*
          The lockup, composed by `BrandLockup` rather than assembled here, so
          the seal, the word, the gap and the accessible name stay identical to
          the footer's and the auth shell's.

          The wordmark is hidden below 480px, and only in this bar: it carries
          the navigation handle, the search field, the create action and the
          account menu, so at 320px the row falls under design-system §8's
          `112px` lockup floor. Showing the word there would either overflow the
          window or draw the lockup under its own minimum. The seal alone is the
          documented reduction, and the name stays in the accessibility tree at
          every width.
        */}
        <BrandLockup
          className="flex shrink-0 items-center gap-2"
          markClassName="h-[2.5rem] w-[2.5rem] shrink-0 sm:hidden"
          lockupClassName="hidden h-[2.5rem] w-[10.36rem] shrink-0 sm:block"
          label={chromeCopy.brandName}
        />
      </Link>

      <span className="ms-auto" />

      <button
        type="button"
        onClick={toggleTheme}
        aria-pressed={theme === 'light'}
        aria-label={theme === 'dark' ? copy.theme.toLight : copy.theme.toDark}
        className="hit-target flex shrink-0 items-center justify-center rounded-sm text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text"
      >
        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>

      <InboxHandle />

      {/*
        The primary create action, and the one control on this page carrying a
        role condition. In practice it is satisfied by construction — every
        session that reaches this host is an operator — so the condition is
        recorded rather than exercised. Icon-only below `--bp-md`, and the label
        is clipped rather than removed so the button keeps its accessible name.
      */}
      <Link
        to="/content/new"
        className="hit-target flex shrink-0 items-center justify-center gap-2 rounded-sm border border-accent bg-accent px-0 text-sm font-semibold text-accent-ink hover:bg-accent-hover active:bg-accent-press md:px-5"
      >
        <PlusIcon />
        <span className="sr-only md:not-sr-only">{chromeCopy.topbar.create}</span>
      </Link>

      <AccountMenu signOut={signOut} />
    </header>
  );
}
